import { aarElementsLabel, categoryLabel, targetSummary } from '../shared/domain';
import { aggregateLabel } from '../shared/score';
import type { ReportStatus } from '../shared/status';
import { laneTitle } from '../shared/lanes';
import {
  deleteAar,
  deleteLane,
  deleteSpot,
  downloadReportPdf,
  fetchAars,
  fetchFeed,
  type Aar,
  type AuthUser,
  type Lane,
  type Spot,
} from './api';
import { bylines, el, fmtTime, formatPlace, go, statusPill, toast } from './ui';

type Filter =
  | 'all'
  | 'flash'
  | 'approved'
  | 'approved-spots'
  | 'approved-lanes'
  | 'aar'
  | 'pending'
  | 'returned'
  | 'draft'
  | 'hq'
  | 'team1'
  | 'team2'
  | 'team3'
  | 'team4'
  | 'enablers';

const FILTERS: {
  id: Filter;
  label: string;
  wide?: boolean;
  tone?: 'green' | 'red' | 'orange' | 'yellow';
}[] = [
  { id: 'flash', label: 'Flash/Incident Reports', wide: true, tone: 'red' },
  { id: 'approved', label: 'All Approved Reports', wide: true, tone: 'green' },
  { id: 'approved-spots', label: 'Approved Spot Reports', tone: 'green' },
  { id: 'approved-lanes', label: 'Approved Lane Reports', tone: 'green' },
  { id: 'aar', label: 'AAR Reports' },
  { id: 'pending', label: 'Reports Pending Review', tone: 'red' },
  { id: 'returned', label: 'Returned for Corrections', tone: 'orange' },
  { id: 'draft', label: 'Draft Reports', tone: 'yellow' },
  { id: 'hq', label: 'HQ Reports' },
  { id: 'team1', label: 'Team 1 Reports' },
  { id: 'team2', label: 'Team 2 Reports' },
  { id: 'team3', label: 'Team 3 Reports' },
  { id: 'team4', label: 'Team 4 Reports' },
  { id: 'enablers', label: 'Enablers Reports' },
  { id: 'all', label: 'View All Reports', wide: true },
];

const EMPTY: Record<Filter, string> = {
  all: 'No reports yet.',
  flash: 'No flash or incident reports.',
  approved: 'No approved reports.',
  'approved-spots': 'No approved spot reports.',
  'approved-lanes': 'No approved lane reports.',
  aar: 'No AAR reports.',
  pending: 'No reports pending review.',
  returned: 'No reports returned for corrections.',
  draft: 'No drafts.',
  hq: 'No HQ reports.',
  team1: 'No Team 1 reports.',
  team2: 'No Team 2 reports.',
  team3: 'No Team 3 reports.',
  team4: 'No Team 4 reports.',
  enablers: 'No Enablers reports.',
};

export function renderBoard(root: HTMLElement, user: AuthUser): () => void {
  let filter = readFilter();
  let spots: Spot[] = [];
  let lanes: Lane[] = [];
  let aars: Aar[] = [];
  let alive = true;

  const dash = el('div', { className: 'dash' });
  const list = el('div', { className: 'stack' });
  const status = el('p', { className: 'muted', textContent: 'Loading…' });
  root.append(el('h1', { textContent: 'Board' }), dash, status, list);

  function paintDash(): void {
    dash.replaceChildren();
    for (const option of FILTERS) {
      const on = filter === option.id;
      const className = [
        'dash-btn',
        option.wide ? 'span-2' : '',
        option.tone ? `tone-${option.tone}` : '',
        on ? 'on' : '',
      ]
        .filter(Boolean)
        .join(' ');
      const btn = el('button', {
        type: 'button',
        className,
        'aria-pressed': on ? 'true' : 'false',
        'data-filter': option.id,
      });
      btn.append(
        el('strong', { textContent: String(tally(option.id)) }),
        el('span', { textContent: option.label })
      );
      btn.addEventListener('click', () => {
        filter = option.id;
        sessionStorage.setItem('mdrc-board-filter', filter);
        paintDash();
        paintList();
      });
      dash.append(btn);
    }
  }

  function tally(id: Filter): number {
    let n = 0;
    for (const spot of spots) if (matches(id, spot, 'spot')) n += 1;
    for (const lane of lanes) if (matches(id, lane, 'lane')) n += 1;
    for (const aar of aars) if (matches(id, aar, 'aar')) n += 1;
    return n;
  }

  function paintList(): void {
    const cards: { at: string; node: HTMLElement }[] = [];
    for (const spot of spots) {
      if (!matches(filter, spot, 'spot')) continue;
      cards.push({ at: spot.observed_at, node: spotCard(spot) });
    }
    for (const lane of lanes) {
      if (!matches(filter, lane, 'lane')) continue;
      cards.push({ at: lane.observed_at, node: laneCard(lane) });
    }
    for (const aar of aars) {
      if (!matches(filter, aar, 'aar')) continue;
      cards.push({ at: aar.observed_at, node: aarCard(aar) });
    }
    cards.sort((a, b) => b.at.localeCompare(a.at));
    if (!cards.length) {
      list.replaceChildren(el('p', { className: 'muted', textContent: EMPTY[filter] }));
      return;
    }
    list.replaceChildren(...cards.map((card) => card.node));
  }

  function spotCard(spot: Spot): HTMLElement {
    const card = el('article', {
      className: spot.flash ? 'feed-card flash' : 'feed-card',
      'data-kind': 'spot',
      'data-id': spot.id,
    });
    const open = el('button', { type: 'button', className: 'feed-open', 'data-action': 'open' });
    const top = el('div', { className: 'kicker' });
    top.append(statusPill(spot.status), el('span', { className: 'pill', textContent: 'Spot' }));
    if (spot.flash) top.append(el('span', { className: 'pill danger', textContent: 'Flash' }));
    top.append(el('span', { textContent: fmtTime(spot.observed_at) }));
    open.append(
      top,
      el('strong', { textContent: categoryLabel(spot.category) }),
      el('span', { textContent: targetSummary(spot.targets) || 'No people tagged' }),
      el('span', { className: 'clamp', textContent: spot.narrative }),
      el('span', {
        className: 'muted',
        textContent: meta(
          formatPlace(spot.location_lat, spot.location_lng, spot.location_description),
          spot.evidence_keys.length
        ),
      }),
      ...bylines(spot)
    );
    open.addEventListener('click', () => go(`board/spot/${spot.id}`));
    card.append(open, sideActions('spots', spot.id, canDelete(spot.author_id)));
    return card;
  }

  function laneCard(lane: Lane): HTMLElement {
    const card = el('article', {
      className: 'feed-card lane-card',
      'data-kind': 'lane',
      'data-id': lane.id,
    });
    const open = el('button', { type: 'button', className: 'feed-open', 'data-action': 'open' });
    const top = el('div', { className: 'kicker' });
    top.append(statusPill(lane.status), el('span', { className: 'pill', textContent: 'Lane' }));
    top.append(el('span', { textContent: fmtTime(lane.observed_at) }));
    const blurb = lane.narrative || lane.strengths || lane.deficiencies || lane.context || 'Lane evaluation';
    open.append(
      top,
      el('strong', { textContent: laneTitle(lane.lane_id) }),
      el('span', { textContent: targetSummary(lane.targets) || 'No people tagged' }),
      el('span', { className: 'clamp', textContent: blurb }),
      el('span', {
        className: 'muted',
        textContent: meta(
          formatPlace(lane.location_lat, lane.location_lng, lane.location_description),
          lane.evidence_keys.length
        ),
      }),
      ...bylines(lane)
    );
    open.addEventListener('click', () => go(`board/lane/${lane.id}`));
    const side = sideActions('lanes', lane.id, canDelete(lane.author_id));
    side.prepend(el('span', { className: 'agg', textContent: aggregateLabel(lane.scores) }));
    card.append(open, side);
    return card;
  }

  function aarCard(aar: Aar): HTMLElement {
    const card = el('article', { className: 'feed-card', 'data-kind': 'aar', 'data-id': aar.id });
    const open = el('button', { type: 'button', className: 'feed-open', 'data-action': 'open' });
    const top = el('div', { className: 'kicker' });
    top.append(statusPill(aar.status), el('span', { className: 'pill', textContent: 'AAR' }));
    top.append(el('span', { textContent: fmtTime(aar.observed_at) }));
    open.append(
      top,
      el('strong', { className: 'aar-topic', textContent: aar.topic || 'AAR' }),
      el('span', { textContent: aarElementsLabel(aar.elements, aar.element) }),
      el('span', {
        className: 'muted',
        textContent: meta(
          formatPlace(aar.location_lat, aar.location_lng, aar.location_description),
          aar.evidence_keys.length
        ),
      }),
      ...bylines(aar)
    );
    open.addEventListener('click', () => go(`board/aar/${aar.id}`));
    card.append(open, sideActions('aars', aar.id, canDelete(aar.author_id)));
    return card;
  }

  function canDelete(authorId: string): boolean {
    return user.role === 'admin' || authorId === user.id;
  }

  function sideActions(
    kind: 'spots' | 'lanes' | 'aars',
    id: string,
    allowDelete: boolean
  ): HTMLElement {
    const side = el('div', { className: 'card-side' });
    const download = el('button', {
      type: 'button',
      className: 'btn',
      textContent: 'Download',
      'aria-label': 'Download PDF',
      'data-action': 'download',
    });
    download.addEventListener('click', () => {
      download.disabled = true;
      void downloadReportPdf(kind, id)
        .catch((err) => toast((err as Error).message, 'err'))
        .finally(() => {
          download.disabled = false;
        });
    });
    side.append(download);
    if (allowDelete) {
      const remove = el('button', {
        type: 'button',
        className: 'btn btn-ghost',
        textContent: 'Delete',
        'data-action': 'delete',
      });
      remove.addEventListener('click', () => {
        if (remove.dataset.armed !== '1') {
          remove.dataset.armed = '1';
          remove.textContent = 'Confirm';
          return;
        }
        remove.disabled = true;
        const drop = kind === 'spots' ? deleteSpot(id) : kind === 'lanes' ? deleteLane(id) : deleteAar(id);
        void drop
          .then(() => refresh())
          .catch((err) => {
            remove.disabled = false;
            toast((err as Error).message, 'err');
          });
      });
      side.append(remove);
    }
    return side;
  }

  async function refresh(): Promise<void> {
    try {
      const feed = await fetchFeed();
      if (!alive) return;
      spots = feed.spots;
      lanes = feed.lanes;
      try {
        aars = await fetchAars();
      } catch (err) {
        aars = [];
        toast((err as Error).message, 'err');
      }
      status.textContent = '';
      paintDash();
      paintList();
    } catch (err) {
      if (!alive) return;
      if (!spots.length && !lanes.length && !aars.length) status.textContent = (err as Error).message;
    }
  }

  paintDash();
  void refresh();
  const timer = window.setInterval(() => {
    if (!document.hidden) void refresh();
  }, 20_000);

  return () => {
    alive = false;
    window.clearInterval(timer);
  };
}

function readFilter(): Filter {
  const raw = sessionStorage.getItem('mdrc-board-filter');
  return FILTERS.some((item) => item.id === raw) ? (raw as Filter) : 'pending';
}

function elementsOf(row: { elements: string[]; element: string }): string[] {
  return row.elements?.length ? row.elements : row.element ? [row.element] : [];
}

function matches(
  filter: Filter,
  row: {
    elements: string[];
    element: string;
    status: ReportStatus;
    flash?: boolean;
    category?: string;
  },
  kind: 'spot' | 'lane' | 'aar'
): boolean {
  if (filter === 'all') return true;
  if (filter === 'flash') return kind === 'spot' && (!!row.flash || row.category === 'incident');
  if (filter === 'aar') return kind === 'aar';
  if (filter === 'approved') return row.status === 'approved';
  if (filter === 'approved-spots') return kind === 'spot' && row.status === 'approved';
  if (filter === 'approved-lanes') return kind === 'lane' && row.status === 'approved';
  if (filter === 'pending') return row.status === 'pending';
  if (filter === 'returned') return row.status === 'returned';
  if (filter === 'draft') return row.status === 'draft';
  return elementsOf(row).includes(filter);
}

function meta(place: string, photos: number): string {
  const bits = [place || 'No place'];
  if (photos) bits.push(`${photos} file${photos === 1 ? '' : 's'}`);
  return bits.join(' · ');
}
